/**
 * Mobile Tab Bar Controller
 * Handles bottom tab navigation for Library ↔ Transcript switching on mobile.
 * Preserves scroll positions when switching tabs.
 */

const MOBILE_BREAKPOINT = 768;

export function createMobileTabs({ document, window }) {
  let tabBar = null;
  let libraryTab = null;
  let transcriptTab = null;
  let isInitialized = false;

  // Scroll position preservation
  const scrollPositions = {
    library: 0,
    transcript: 0,
  };

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function getCurrentView() {
    if (document.body.classList.contains("showing-transcript")) {
      return "transcript";
    }
    if (document.body.classList.contains("library-mode")) {
      return "library";
    }
    return null;
  }

  function saveScrollPosition(view) {
    if (view === "library") {
      const viewport = document.querySelector(
        "#libraryView .custom-scroll__viewport",
      );
      if (viewport) {
        scrollPositions.library = viewport.scrollTop;
      }
    } else if (view === "transcript") {
      const viewport = document.querySelector(
        "#transcriptView .custom-scroll__viewport",
      );
      if (viewport) {
        scrollPositions.transcript = viewport.scrollTop;
      }
    }
  }

  function restoreScrollPosition(view) {
    requestAnimationFrame(() => {
      if (view === "library") {
        const viewport = document.querySelector(
          "#libraryView .custom-scroll__viewport",
        );
        if (viewport) {
          viewport.scrollTop = scrollPositions.library;
        }
      } else if (view === "transcript") {
        const viewport = document.querySelector(
          "#transcriptView .custom-scroll__viewport",
        );
        if (viewport) {
          viewport.scrollTop = scrollPositions.transcript;
        }
      }
    });
  }

  function updateActiveTab(view) {
    if (!libraryTab || !transcriptTab) return;

    libraryTab.classList.toggle("mobile-tab--active", view === "library");
    transcriptTab.classList.toggle("mobile-tab--active", view === "transcript");
  }

  function setTranscriptAvailable(available) {
    if (!tabBar) return;
    tabBar.dataset.transcriptAvailable = available ? "true" : "false";
  }

  function switchToLibrary() {
    if (getCurrentView() === "library") return;

    // Save current scroll position before switching
    saveScrollPosition("transcript");

    // Use existing returnToLibrary if available
    if (typeof window.returnToLibrary === "function") {
      window.returnToLibrary();
    } else {
      // Fallback: directly toggle classes
      document.body.classList.remove("showing-transcript");
      document.body.classList.add("library-mode");
      const libraryView = document.getElementById("libraryView");
      const transcriptView = document.getElementById("transcriptView");
      if (libraryView) libraryView.classList.add("active");
      if (transcriptView) transcriptView.setAttribute("aria-hidden", "true");
    }

    updateActiveTab("library");
    restoreScrollPosition("library");
  }

  function switchToTranscript() {
    if (getCurrentView() === "transcript") return;

    // Check if transcript is available
    const transcriptContent = document.getElementById("transcriptContent");
    if (!transcriptContent || !transcriptContent.textContent.trim()) {
      return; // No transcript to show
    }

    // Save current scroll position before switching
    saveScrollPosition("library");

    // Toggle view state
    document.body.classList.remove("library-mode");
    document.body.classList.add("showing-transcript");
    const libraryView = document.getElementById("libraryView");
    const transcriptView = document.getElementById("transcriptView");
    if (libraryView) libraryView.classList.remove("active");
    if (transcriptView) transcriptView.setAttribute("aria-hidden", "false");

    updateActiveTab("transcript");
    restoreScrollPosition("transcript");
  }

  function handleTabClick(event) {
    const tab = event.currentTarget;
    const tabType = tab.dataset.tab;

    if (tabType === "library") {
      switchToLibrary();
    } else if (tabType === "transcript") {
      switchToTranscript();
    }
  }

  function handleResize() {
    if (!tabBar) return;

    if (isMobile()) {
      tabBar.style.display = "";
    } else {
      tabBar.style.display = "none";
    }
  }

  function syncWithViewState() {
    const currentView = getCurrentView();
    updateActiveTab(currentView);

    // Check if transcript content exists
    const transcriptContent = document.getElementById("transcriptContent");
    const hasTranscript =
      transcriptContent && transcriptContent.textContent.trim().length > 0;
    setTranscriptAvailable(hasTranscript);
  }

  // Watch for body class changes to auto-sync tab state
  let bodyObserver = null;

  function setupBodyObserver() {
    if (bodyObserver) return;

    bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          syncWithViewState();
          break;
        }
      }
    });

    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  function init() {
    if (isInitialized) return;

    tabBar = document.getElementById("mobileTabBar");
    if (!tabBar) {
      console.warn("[mobileTabs] Tab bar element not found");
      return;
    }

    libraryTab = tabBar.querySelector('[data-tab="library"]');
    transcriptTab = tabBar.querySelector('[data-tab="transcript"]');

    if (libraryTab) {
      libraryTab.addEventListener("click", handleTabClick);
    }
    if (transcriptTab) {
      transcriptTab.addEventListener("click", handleTabClick);
    }

    // Handle resize for orientation changes
    window.addEventListener("resize", handleResize);

    // Watch for view state changes via body class
    setupBodyObserver();

    // Initial sync
    syncWithViewState();
    handleResize();

    isInitialized = true;
  }

  function destroy() {
    if (!isInitialized) return;

    if (libraryTab) {
      libraryTab.removeEventListener("click", handleTabClick);
    }
    if (transcriptTab) {
      transcriptTab.removeEventListener("click", handleTabClick);
    }
    window.removeEventListener("resize", handleResize);

    if (bodyObserver) {
      bodyObserver.disconnect();
      bodyObserver = null;
    }

    tabBar = null;
    libraryTab = null;
    transcriptTab = null;
    isInitialized = false;
  }

  return {
    init,
    destroy,
    syncWithViewState,
    setTranscriptAvailable,
    isMobile,
  };
}
